import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

export function Signup() {
  const [form, setForm] = useState({ name: '', contactName: '', contactEmail: '' });
  const [done, setDone] = useState<{ slug: string; emailSent: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<{ tenant: { slug: string }; emailSent?: boolean }>('/tenants/signup', form);
      setDone({ slug: res.tenant.slug, emailSent: !!res.emailSent });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed');
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div className="min-h-screen bg-ink-50 grid place-items-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-brand-50 text-brand-600 grid place-items-center mx-auto mb-4">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">You're set up.</h1>
          <p className="mt-3 text-ink-600">
            Your HOA is registered as{' '}
            <code className="font-mono text-sm bg-ink-100 text-ink-900 px-2 py-0.5 rounded">{done.slug}</code>.
          </p>
          {done.emailSent ? (
            <p className="mt-3 text-ink-600 text-sm">
              Check your inbox at <strong>{form.contactEmail}</strong> for the sign-in link.
            </p>
          ) : (
            <p className="mt-3 text-amber-700 text-sm">
              We couldn't send the sign-in email automatically. Visit <Link to="/login" className="underline">the sign-in page</Link> to request one.
            </p>
          )}
          <Link to="/login" className="mt-6 inline-block text-brand-700 font-medium hover:underline">
            Go to sign-in →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-50 grid place-items-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 font-semibold text-lg tracking-tight text-ink-900 mb-8">
          <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
            <rect width="32" height="32" rx="7" fill="#0f172a" />
            <path d="M9 16l5 5 9-11" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          HOA-Vote
        </Link>
        <div className="bg-white border border-ink-200 rounded-xl shadow-card p-6 sm:p-8">
          <h1 className="text-2xl font-semibold tracking-tight">Register your HOA</h1>
          <p className="mt-1 text-sm text-ink-600">Free to start. No credit card required.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <Field label="HOA name">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Magnolia Grove HOA"
                className="mt-1.5 w-full border border-ink-200 rounded-lg px-3 py-2.5 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition outline-none" />
            </Field>
            <Field label="Your name">
              <input required value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                className="mt-1.5 w-full border border-ink-200 rounded-lg px-3 py-2.5 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition outline-none" />
            </Field>
            <Field label="Your email">
              <input type="email" required value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                className="mt-1.5 w-full border border-ink-200 rounded-lg px-3 py-2.5 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition outline-none" />
            </Field>
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button type="submit" disabled={loading}
              className="w-full bg-ink-900 text-white py-2.5 rounded-lg font-medium hover:bg-ink-800 transition disabled:opacity-50">
              {loading ? 'Creating…' : 'Create HOA'}
            </button>
            <p className="text-xs text-ink-500 text-center">You'll be the first board admin. Invite the rest of the board after signing in.</p>
          </form>
        </div>
        <p className="text-center text-sm text-ink-500 mt-5">
          Already registered? <Link to="/login" className="text-ink-900 font-medium hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-ink-800">{label}</span>
      {children}
    </label>
  );
}
